import { Box, Typography } from "@mui/material";
import { useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import useColors from "../../../../../api/hooks/useColors";
import LoadingIcon from "../../../../../components/LoadingIcon";
import StyledDataGrid from "../../../../../components/StyledDataGrid";
import EntityDrawer from "../../../../../scenes/stack/environment/components/EntityDrawer/EntityDrawer";
import { ComplexEntityTypes, QueryParam } from "../../../../../utils/constants";
import { useLocalTime } from "../../../../../utils/timestamp";
import { getNHIColumns } from "../../../../stack/environment/entitiesTable/nhiColumns";
import { getSharedColumns } from "../../../../stack/environment/entitiesTable/sharedColumns";
import { useEntities } from "../../../../stack/environment/hooks/useEntities";
import WidgetWrapper from "../../../custom_dashboard/WidgetWrapper";

const NHITableWidget = () => {
  const colors = useColors();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const showLocalTime = useLocalTime();

  const { useQueryForEntitiesByType } = useEntities();
  const { data: nhiEntities, isLoading } = useQueryForEntitiesByType({
    type: ComplexEntityTypes.NONHUMAN_IDENTITY,
  });

  const selectedEntity = useMemo(
    () => searchParams.get(QueryParam.entityId),
    [searchParams]
  );
  const entity = useMemo(
    () => nhiEntities?.find((entity) => entity.unique_id === selectedEntity),
    [nhiEntities, selectedEntity]
  );
  const openDrawer = useMemo(() => Boolean(selectedEntity), [selectedEntity]);

  const columns = [...getSharedColumns([])].filter(
    (column) =>
      column.field === "display_name" ||
      column.field === "risk" ||
      column.field === "sources"
  );

  const nhiColumns = getNHIColumns(colors, showLocalTime);
  columns.splice(3, 0, nhiColumns.type);

  columns.push(nhiColumns.lastUsed);
  const onCellClick = (params) => {
    const updatedSearchParams = new URLSearchParams(searchParams);
    updatedSearchParams.set(QueryParam.entityId, params.row.unique_id);
    setSearchParams(updatedSearchParams);
  };

  const handleViewAll = () => {
    navigate(
      `/inventory/environment?${QueryParam.environmentType}=${ComplexEntityTypes.NONHUMAN_IDENTITY}`
    );
  };

  const onDrawerClose = () => {
    const updatedSearchParams = new URLSearchParams(searchParams);
    updatedSearchParams.delete(QueryParam.entityId);
    setSearchParams(updatedSearchParams);
  };

  return (
    <WidgetWrapper
      title="NHI Inventory"
      info="NHIs across your environment"
      viewAll={{
        handleViewAll: handleViewAll,
        tooltip: "View all NHIs in the environment",
        newTab: false,
      }}
    >
      {isLoading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "300px",
          }}
        >
          <LoadingIcon size={12} />
        </Box>
      ) : !nhiEntities || nhiEntities.length === 0 ? (
        <Box sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="caption" color="text.secondary">
            No NHI entities found
          </Typography>
        </Box>
      ) : (
        <Box sx={{ height: 400 }}>
          <StyledDataGrid
            columns={columns}
            rows={nhiEntities}
            loading={isLoading}
            onCellClick={onCellClick}
            getRowId={(row) => row.entity_id + "-" + row.customer_id}
            initialState={{
              sorting: {
                sortModel: [{ field: "risk", sort: "desc" }],
              },
              pagination: {
                paginationModel: { pageSize: 5 },
              },
            }}
            pageSizeOptionsOverride={[5, 10]}
            checkboxSelection={false}
            disableRowSelectionOnClick
            boxSXProps={{
              width: "100%",
              border: "none",
              ".MuiDataGrid-root": {
                border: "none",
                boxShadow: "none",
                "& .MuiDataGrid-cell:first-of-type, & .MuiDataGrid-columnHeader:first-of-type":
                  {
                    paddingLeft: "16px",
                  },
                "& .MuiDataGrid-cell:last-of-type, & .MuiDataGrid-columnHeader:last-of-type":
                  {
                    paddingRight: "16px",
                  },
                "& .MuiDataGrid-virtualScroller": {
                  minHeight: "270px",
                },
              },
            }}
          />
          <EntityDrawer
            openDrawer={openDrawer}
            onClose={onDrawerClose}
            selectedEntity={entity}
          />
        </Box>
      )}
    </WidgetWrapper>
  );
};

export default NHITableWidget;
